/**
 * formActions
 *
 * Imperative mutators for `formStore.state`. Kept separate from the state
 * proxy so consumers can call these without importing the proxy directly.
 * Navigation delegates to the `navigate` reference installed by
 * `formStoreUrlListener`; selection-via-URL is the source of truth and the
 * state follows the URL.
 */

import type { VisibilityTypeId } from '#types/System.types';
import { createSplitQueryFromOption, type FormSplitOption } from '../../use/form/useFormSplit';
import { archiveArtifact } from '../../services/artifactFunctions';
import { formStore } from './formStore';

const ensureNavigate = (): NonNullable<typeof formStore.navigate> => {
    const navigate = formStore.navigate;
    if (!navigate) {
        throw new Error('[formStore] navigate is not installed — ensure formStoreUrlListener has been imported');
    }
    return navigate;
};

const navigateToForm = (formId: string): void => {
    ensureNavigate()(`/forms/${formId}`);
};

const navigateToVersion = (formId: string, versionId: string): void => {
    ensureNavigate()(`/forms/${formId}/formVersions/${versionId}`);
};

const selectUILayout = (uiLayoutId: string | undefined): void => {
    formStore.state.uiLayoutId = uiLayoutId;
};

const recordSessionView = (mode: VisibilityTypeId): void => {
    const formTypeId = formStore.state.form.data?.formType;
    if (formTypeId) formStore.state.sessionViewByFormType[formTypeId] = mode;
};

const setVisibilityMode = (mode: VisibilityTypeId): void => {
    recordSessionView(mode);
    formStore.state.visibilityMode = mode;
};

/**
 * Sole writer of the split selection outside `formStore`'s own reset paths.
 * Takes the option object rather than its value so `splitQuery` can be stored
 * alongside it: `undefined`, a `no-split` option, or an option missing
 * `splitRef`/`actualValue` all land on no-split with no filter.
 *
 * The two properties are derived from one `createSplitQueryFromOption` result so
 * the `splitQuery`-iff-`show-value` invariant holds by construction — a
 * malformed option cannot leave a selected value naming a filter that is not
 * there.
 */
const setSelectedSplitOption = (option: FormSplitOption | undefined): void => {
    const splitQuery = createSplitQueryFromOption(option);
    formStore.state.selectedSplitOption = option && splitQuery ? option.value : 'no-split';
    formStore.state.splitQuery = splitQuery;
};

const archiveForm = async (formId: string): Promise<boolean> => {
    formStore.state.isArchivingForm = true;
    try {
        await archiveArtifact({ artifactType: 'form', artifactId: formId, action: 'archive' });
        return true;
    } catch (error) {
        console.error('Failed to archive form:', error);
        return false;
    } finally {
        formStore.state.isArchivingForm = false;
    }
};

const unarchiveForm = async (formId: string): Promise<boolean> => {
    formStore.state.isUnarchivingForm = true;
    try {
        await archiveArtifact({ artifactType: 'form', artifactId: formId, action: 'unarchive' });
        return true;
    } catch (error) {
        console.error('Failed to unarchive form:', error);
        return false;
    } finally {
        formStore.state.isUnarchivingForm = false;
    }
};

const archiveFormVersion = async (
    formId: string,
    versionId: string,
): Promise<{ success: boolean; error?: string }> => {
    formStore.state.isArchiving = true;
    try {
        await archiveArtifact({
            artifactType: 'formVersion',
            artifactId: versionId,
            parentId: formId,
            action: 'archive',
        });
        return { success: true };
    } catch (error: unknown) {
        console.error('Failed to archive form version:', error);
        const message = error instanceof Error ? error.message : 'Failed to archive form version';
        return { success: false, error: message };
    } finally {
        formStore.state.isArchiving = false;
    }
};

const unarchiveFormVersion = async (
    versionId: string,
    formId?: string,
): Promise<{ success: boolean; error?: string }> => {
    formStore.state.isUnarchiving = true;
    try {
        await archiveArtifact({
            artifactType: 'formVersion',
            artifactId: versionId,
            parentId: formId,
            action: 'unarchive',
        });
        return { success: true };
    } catch (error: unknown) {
        console.error('Failed to unarchive form version:', error);
        const message = error instanceof Error ? error.message : 'Failed to unarchive form version';
        return { success: false, error: message };
    } finally {
        formStore.state.isUnarchiving = false;
    }
};

export const formActions = {
    navigateToForm,
    navigateToVersion,
    selectUILayout,
    setVisibilityMode,
    setSelectedSplitOption,
    archiveForm,
    unarchiveForm,
    archiveFormVersion,
    unarchiveFormVersion,
};
